import { useState, useEffect, useRef } from 'react';
import api, { API_ORIGIN } from '../api/client';
import BodyStylePreview, { BODY_STYLE_DEFAULTS } from '../components/BodyStylePreview';
import HtmlEditor from '../components/HtmlEditor';
import { applySiteInfo } from '../utils/siteInfo';
import { LEGACY_MENU_ALIASES_MAP, LEGACY_ACTION_ALIASES_MAP } from '../context/PermissionContext';

// 저장된 권한 객체에서 구 키를 신규 키로 변환.
// 구 키가 true 면 해당 신규 키도 true. 신규 키만 남기고 모든 구 키 제거.
function normalizePerms(saved, aliasMap, validKeys) {
  if (!saved || saved.all === true) return saved;
  const out = {};
  for (const k of validKeys) if (saved[k]) out[k] = true;
  for (const [newKey, oldKeys] of Object.entries(aliasMap)) {
    if (oldKeys.some(ok => saved[ok])) out[newKey] = true;
  }
  return out;
}

const tabList = [
  { key: 'template', label: '템플릿 관리' },
  { key: 'estimate', label: '견적서 설정' },
  { key: 'groups', label: '그룹 관리' },
  { key: 'partproduct', label: '부품/제품 설정' },
  { key: 'backup', label: '백업/복구' },
  { key: 'email', label: '이메일 알림' },
  { key: 'site', label: '사이트 관리' },
  { key: 'patch', label: '패치 관리' },
];


/* ===== 2. 템플릿 관리 ===== */
function TemplateTab() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const defForm = { name:'', header_html:'', footer_html:'', payment_terms:'', delivery_terms:'', warranty_terms:'', show_consumer_price:true, show_consumer_amount:false, vat_separate:true, notes:'', valid_days:14, pdf_margin_top:15, pdf_margin_bottom:20, pdf_margin_left:15, pdf_margin_right:15, spacing_after_header:8, spacing_before_footer:8 };
  const [form, setForm] = useState({...defForm});
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => { load(); }, []);
  const load = () => api.get('/settings/templates/all').then(setTemplates).catch(console.error);

  const handleSave = async () => {
    try {
      if (editing) { await api.put(`/settings/templates/${editing.id}`, form); }
      else { await api.post('/settings/templates', form); }
      setEditing(null); setShowAdd(false); setForm({...defForm}); load();
    } catch (e) { alert(e.message); }
  };

  // 복제: 기본 템플릿 표시(is_default)와 id/날짜를 제외한 모든 설정을 그대로 복사
  const handleClone = async (t) => {
    try {
      const { id, created_at, updated_at, is_default, ...rest } = t;
      await api.post('/settings/templates', { ...rest, name: t.name + ' (복사)', is_default: false });
      load();
    } catch (e) { alert(e.message); }
  };

  const handleDefault = async (id) => { await api.put(`/settings/templates/${id}`, { is_default: true }); load(); };
  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try { await api.del(`/settings/templates/${id}`); load(); } catch (e) { alert(e.message); }
  };

  const openEdit = (t) => { setEditing(t); setForm({ ...defForm, name:t.name, header_html:t.header_html||'', footer_html:t.footer_html||'', payment_terms:t.payment_terms||'', delivery_terms:t.delivery_terms||'', warranty_terms:t.warranty_terms||'', show_consumer_price:t.show_consumer_price!==false, show_consumer_amount:t.show_consumer_amount===true, vat_separate:t.vat_separate!==false, notes:t.notes||'', valid_days:t.valid_days||14, pdf_margin_top:t.pdf_margin_top??15, pdf_margin_bottom:t.pdf_margin_bottom??20, pdf_margin_left:t.pdf_margin_left??15, pdf_margin_right:t.pdf_margin_right??15, spacing_after_header:t.spacing_after_header??8, spacing_before_footer:t.spacing_before_footer??8 }); setShowAdd(false); };

  return (
    <div>
      <div className="card">
        <div className="card-header"><div className="card-title">견적서 템플릿 목록</div><button className="btn btn-primary btn-sm" onClick={() => { setShowAdd(true); setEditing(null); setForm({...defForm}); }}>+ 새 템플릿</button></div>
        <div style={{overflowX:'auto'}}><table>
          <thead><tr><th>템플릿명</th><th>생성일</th><th>상태</th><th>작업</th></tr></thead>
          <tbody>
            {templates.map(t => (
              <tr key={t.id}>
                <td className="fw-bold">{t.name}</td>
                <td>{t.created_at?.slice(0, 10)}</td>
                <td>{t.is_default && <span className="badge badge-blue">★ 기본</span>}</td>
                <td><div className="btn-group">
                  <button className="btn btn-outline btn-sm" onClick={() => openEdit(t)}>편집</button>
                  <button className="btn btn-outline btn-sm" onClick={() => handleClone(t)}>복제</button>
                  {!t.is_default && <button className="btn btn-outline btn-sm" onClick={() => handleDefault(t.id)}>기본 설정</button>}
                  {!t.is_default && <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t.id)}>삭제</button>}
                </div></td>
              </tr>
            ))}
            {templates.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>등록된 템플릿이 없습니다.</td></tr>}
          </tbody>
        </table></div>
      </div>

      {(editing || showAdd) && (
        <TemplateEditor form={form} setForm={setForm} editing={editing}
          onSave={handleSave} onCancel={() => { setEditing(null); setShowAdd(false); }} />
      )}
    </div>
  );
}

/* ===== 3. 견적서 설정 ===== */
const TEMPLATE_VARS = [
  { label: '고객사명', value: '{{고객사명}}' },
  { label: '수신자', value: '{{수신자}}' },
  { label: '사업명', value: '{{사업명}}' },
  { label: '견적합계(한글,VAT포함)', value: '{{견적합계_한글_VAT포함}}' },
  { label: '견적합계(한글,VAT미포함)', value: '{{견적합계_한글_VAT미포함}}' },
  { label: '견적합계(숫자,VAT포함)', value: '{{견적합계_숫자_VAT포함}}' },
  { label: '견적합계(숫자,VAT미포함)', value: '{{견적합계_숫자_VAT미포함}}' },
  { label: '견적년월일', value: '{{견적년월일}}' },
  { label: '견적유효기간', value: '{{견적유효기간}}' },
  { label: '최종사용자', value: '{{최종사용자}}' },
  { label: '견적번호', value: '{{견적번호}}' },
  { label: '결제조건', value: '{{결제조건}}' },
  { label: '납기', value: '{{납기}}' },
  { label: '무상하자보증기간', value: '{{무상하자보증기간}}' },
  { label: '비고', value: '{{비고}}' },
  { label: '담당자 이름', value: '{{담당자_이름}}' },
  { label: '담당자 직급', value: '{{담당자_직급}}' },
  { label: '담당자 휴대전화', value: '{{견적담당자_전화}}' },
  { label: '담당자 이메일', value: '{{견적담당자_메일}}' },
];

const PREVIEW_SAMPLE = {
  '{{고객사명}}':'ABC Trading Co., Ltd.', '{{수신자}}':'홍길동', '{{사업명}}':'OO기관 네트워크 구축 사업',
  '{{견적합계_한글_VAT포함}}':'일금 삼천사백구십팔만원정', '{{견적합계_한글_VAT미포함}}':'일금 삼천백팔십만원정',
  '{{견적합계_숫자_VAT포함}}':'₩34,980,000', '{{견적합계_숫자_VAT미포함}}':'₩31,800,000',
  '{{견적년월일}}':'2026년 05월 16일', '{{견적유효기간}}':'2026년 06월 16일',
  '{{최종사용자}}':'한국통신 주식회사', '{{견적번호}}':'INT-260516-KCS-0001',
  '{{결제조건}}':'선금 50%, 납품 후 잔금 50%',
  '{{납기}}':'발주 후 14일', '{{무상하자보증기간}}':'납품 후 1년',
  '{{비고}}':'본 견적 금액은 설치비 별도입니다.<br>설치 및 초기 세팅 지원이 포함되어 있습니다.',
  '{{담당자_이름}}':'김철수', '{{담당자_직급}}':'대리',
  '{{견적담당자_전화}}':'010-1234-5678', '{{견적담당자_메일}}':'kim@soosan.co.kr',
};

function FullPreview({ headerHtml, footerHtml, onClose }) {
  const replaceVars = (html) => {
    let result = html || '';
    Object.entries(PREVIEW_SAMPLE).forEach(([k,v]) => { result = result.replaceAll(k, v); });
    TEMPLATE_VARS.forEach(tv => { result = result.replaceAll(tv.value, PREVIEW_SAMPLE[tv.value] || tv.value); });
    return result;
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'flex-start',justifyContent:'center',overflowY:'auto',padding:'20px 0'}}>
      <div style={{width:780}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,padding:'0 10px'}}>
          <div style={{fontSize:14,fontWeight:700,color:'#fff'}}>견적서 미리보기 <span style={{fontSize:11,fontWeight:400,color:'#94a3b8',marginLeft:8}}>변수는 샘플 데이터로 대체</span></div>
          <button className="btn btn-outline btn-sm" style={{background:'#fff'}} onClick={onClose}>닫기 ✕</button>
        </div>
        <div style={{
          width:'210mm', margin:'0 auto', background:'#fff', padding:'15mm',
          boxShadow:'0 2px 12px rgba(0,0,0,0.12)',
          fontFamily:"'Pretendard',-apple-system,'Malgun Gothic',sans-serif",
          fontSize:11, color:'#222', lineHeight:1.6,
        }}>
          {/* 헤더 영역 (사용자 편집 내용) */}
          <div dangerouslySetInnerHTML={{__html:replaceVars(headerHtml)}} style={{marginBottom:'5mm'}} />

          {/* 견적항목 (시스템 자동 생성 예시) */}
          <table style={{width:'100%',borderCollapse:'collapse',marginBottom:'5mm'}}>
            <thead><tr>
              {['No','품목','세부사항','견적단가','수량','합계'].map(h=>(
                <th key={h} style={{background:'#1e293b',color:'#fff',fontSize:9,fontWeight:600,padding:'5px 6px',textAlign:h==='견적단가'||h==='합계'?'right':h==='수량'||h==='No'?'center':'left'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              <tr>
                <td style={{border:'1px solid #d1d5db',padding:6,textAlign:'center',fontSize:10}}>1</td>
                <td style={{border:'1px solid #d1d5db',padding:6,fontSize:10}}><strong>SPA-3100</strong><br/><span style={{fontSize:9,color:'#64748b'}}>SPA-3100-A2</span></td>
                <td style={{border:'1px solid #d1d5db',padding:6,fontSize:9}}>처리속도: 500MB/s<br/>RAM: 64GB DDR5<br/>Storage: 2TB SSD</td>
                <td style={{border:'1px solid #d1d5db',padding:6,textAlign:'right',fontSize:10,fontWeight:700}}>12,000,000</td>
                <td style={{border:'1px solid #d1d5db',padding:6,textAlign:'center',fontSize:10}}>2</td>
                <td style={{border:'1px solid #d1d5db',padding:6,textAlign:'right',fontSize:10,fontWeight:700}}>24,000,000</td>
              </tr>
              <tr>
                <td style={{border:'1px solid #d1d5db',padding:6,textAlign:'center',fontSize:10}}>2</td>
                <td style={{border:'1px solid #d1d5db',padding:6,fontSize:10}}><strong>SPA-1000</strong><br/><span style={{fontSize:9,color:'#64748b'}}>SPA-1000-B1</span></td>
                <td style={{border:'1px solid #d1d5db',padding:6,fontSize:9}}>처리속도: 300MB/s<br/>RAM: 16GB DDR5</td>
                <td style={{border:'1px solid #d1d5db',padding:6,textAlign:'right',fontSize:10,fontWeight:700}}>4,500,000</td>
                <td style={{border:'1px solid #d1d5db',padding:6,textAlign:'center',fontSize:10}}>1</td>
                <td style={{border:'1px solid #d1d5db',padding:6,textAlign:'right',fontSize:10,fontWeight:700}}>4,500,000</td>
              </tr>
              <tr style={{borderTop:'2px solid #1e293b'}}>
                <td colSpan={5} style={{padding:6,textAlign:'right',fontWeight:700,fontSize:11,background:'#f8fafc'}}>합 계</td>
                <td style={{padding:6,textAlign:'right',fontWeight:800,fontSize:11,background:'#f8fafc'}}>28,500,000</td>
              </tr>
              <tr><td colSpan={5} style={{padding:5,textAlign:'right',fontSize:10,color:'#64748b',background:'#f8fafc'}}>부가세 (10%)</td>
                <td style={{padding:5,textAlign:'right',fontSize:10,color:'#64748b',background:'#f8fafc'}}>2,850,000</td></tr>
              <tr><td colSpan={5} style={{padding:6,textAlign:'right',fontWeight:800,fontSize:11,background:'#eff6ff'}}>부가세 포함</td>
                <td style={{padding:6,textAlign:'right',fontWeight:800,fontSize:12,background:'#eff6ff',color:'#1e293b'}}>31,350,000</td></tr>
            </tbody>
          </table>

          {/* 푸터 영역 (사용자 편집 내용) */}
          <div dangerouslySetInnerHTML={{__html:replaceVars(footerHtml)}} style={{marginTop:'5mm'}} />
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ form, setForm, editing, onSave, onCancel }) {
  const [showFullPreview, setShowFullPreview] = useState(false);

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div className="card-title">{editing ? `편집: ${editing.name}` : '새 템플릿'}</div>
          <button className="btn btn-outline btn-sm" onClick={()=>setShowFullPreview(true)}>👁 견적서 미리보기</button>
        </div>
        <div className="form-group"><label className="form-label">템플릿명</label><input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
      </div>

      {/* 견적서 기본값 설정 */}
      <div className="card">
        <div className="card-title" style={{marginBottom:12}}>견적서 기본값</div>
        <div className="form-row">
          <div className="form-group" style={{flex:1}}><label className="form-label">결제조건</label><input className="form-input" value={form.payment_terms} onChange={e=>setForm({...form,payment_terms:e.target.value})} /></div>
          <div className="form-group" style={{flex:1}}><label className="form-label">납기</label><input className="form-input" value={form.delivery_terms} onChange={e=>setForm({...form,delivery_terms:e.target.value})} /></div>
          <div className="form-group" style={{flex:1}}><label className="form-label">무상하자보증기간</label><input className="form-input" value={form.warranty_terms} onChange={e=>setForm({...form,warranty_terms:e.target.value})} /></div>
        </div>
        <div className="form-row">
          <div className="form-group" style={{flex:1}}><label className="form-label">견적유효기간 (일)</label><input type="number" className="form-input" min={1} max={365} value={form.valid_days} onChange={e=>setForm({...form,valid_days:parseInt(e.target.value)||14})} /></div>
          <div className="form-group" style={{flex:2,display:'flex',alignItems:'center',gap:20,paddingTop:20}}>
            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#64748b',cursor:'pointer'}}>소비자단가 <div className={`toggle ${form.show_consumer_price?'on':''}`} onClick={()=>{if(form.show_consumer_price)setForm({...form,show_consumer_price:false,show_consumer_amount:false});else setForm({...form,show_consumer_price:true});}}><div className="toggle-knob"></div></div></label>
            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:form.show_consumer_price?'#64748b':'#cbd5e1',cursor:form.show_consumer_price?'pointer':'default'}}>소비자금액 <div className={`toggle ${form.show_consumer_amount&&form.show_consumer_price?'on':''}`} onClick={()=>{if(form.show_consumer_price)setForm({...form,show_consumer_amount:!form.show_consumer_amount});}} style={!form.show_consumer_price?{opacity:0.4,pointerEvents:'none'}:{}}><div className="toggle-knob"></div></div></label>
            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#64748b',cursor:'pointer'}}>부가세 별도 <div className={`toggle ${form.vat_separate?'on':''}`} onClick={()=>setForm({...form,vat_separate:!form.vat_separate})}><div className="toggle-knob"></div></div></label>
          </div>
        </div>
        <div className="form-group"><label className="form-label">비고</label><textarea className="form-input" rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} /></div>
        <div style={{marginTop:12,marginBottom:4,fontSize:12,fontWeight:600,color:'#475569'}}>PDF 여백 (mm)</div>
        <div className="form-row" style={{gap:8}}>
          <div className="form-group" style={{flex:1}}><label className="form-label" style={{fontSize:11}}>위</label><input type="number" className="form-input" min={0} max={50} value={form.pdf_margin_top} onChange={e=>setForm({...form,pdf_margin_top:parseInt(e.target.value)||0})} /></div>
          <div className="form-group" style={{flex:1}}><label className="form-label" style={{fontSize:11}}>아래</label><input type="number" className="form-input" min={0} max={50} value={form.pdf_margin_bottom} onChange={e=>setForm({...form,pdf_margin_bottom:parseInt(e.target.value)||0})} /></div>
          <div className="form-group" style={{flex:1}}><label className="form-label" style={{fontSize:11}}>왼쪽</label><input type="number" className="form-input" min={0} max={50} value={form.pdf_margin_left} onChange={e=>setForm({...form,pdf_margin_left:parseInt(e.target.value)||0})} /></div>
          <div className="form-group" style={{flex:1}}><label className="form-label" style={{fontSize:11}}>오른쪽</label><input type="number" className="form-input" min={0} max={50} value={form.pdf_margin_right} onChange={e=>setForm({...form,pdf_margin_right:parseInt(e.target.value)||0})} /></div>
        </div>
        <div style={{marginTop:12,marginBottom:4,fontSize:12,fontWeight:600,color:'#475569'}}>섹션 간격 (mm)</div>
        <div className="form-row" style={{gap:8}}>
          <div className="form-group" style={{flex:1}}><label className="form-label" style={{fontSize:11}}>헤더 → 본문</label><input type="number" className="form-input" min={0} max={50} value={form.spacing_after_header} onChange={e=>setForm({...form,spacing_after_header:parseInt(e.target.value)||0})} /></div>
          <div className="form-group" style={{flex:1}}><label className="form-label" style={{fontSize:11}}>본문 → 푸터</label><input type="number" className="form-input" min={0} max={50} value={form.spacing_before_footer} onChange={e=>setForm({...form,spacing_before_footer:parseInt(e.target.value)||0})} /></div>
          <div style={{flex:2}} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={onSave}>💾 기본값 저장</button>
      </div>

      <HtmlEditor label="헤더 편집" value={form.header_html} onChange={v => setForm({ ...form, header_html: v })} onSave={onSave} />
      <BodyStyleEditor />
      <HtmlEditor label="푸터 편집" value={form.footer_html} onChange={v => setForm({ ...form, footer_html: v })} onSave={onSave} />

      {showFullPreview && <FullPreview headerHtml={form.header_html} footerHtml={form.footer_html} onClose={()=>setShowFullPreview(false)} />}
    </div>
  );
}

function BodyStyleEditor() {
  const [bodyStyle, setBodyStyle] = useState({...BODY_STYLE_DEFAULTS});
  const [bsSaving, setBsSaving] = useState(false);
  const [bsMsg, setBsMsg] = useState('');

  useEffect(() => {
    api.get('/settings').then(data => {
      if (data.estimate_body_style) {
        try { setBodyStyle({...BODY_STYLE_DEFAULTS, ...(typeof data.estimate_body_style === 'string' ? JSON.parse(data.estimate_body_style) : data.estimate_body_style)}); } catch(e) {}
      }
    });
  }, []);

  return (
    <div className="card">
      <div className="card-title mb-8">본문 서식 (항목 테이블)</div>
      {bsMsg && <div className="alert alert-info" style={{marginBottom:12}}>{bsMsg}</div>}
      <div style={{display:'flex',gap:24,flexWrap:'wrap'}}>
        <div style={{flex:1,minWidth:280}}>
          <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:8,paddingBottom:4,borderBottom:'1px solid #e2e8f0'}}>테이블 헤더</div>
          <div className="form-row" style={{marginBottom:8}}>
            <div className="form-group" style={{flex:1}}><label className="form-label">배경색</label><div style={{display:'flex',gap:6,alignItems:'center'}}><input type="color" value={bodyStyle.header_bg} onChange={e=>setBodyStyle({...bodyStyle,header_bg:e.target.value})} style={{width:32,height:28,border:'1px solid #d1d5db',borderRadius:4,padding:0,cursor:'pointer'}} /><input className="form-input" value={bodyStyle.header_bg} onChange={e=>setBodyStyle({...bodyStyle,header_bg:e.target.value})} style={{flex:1,fontSize:11}} /></div></div>
            <div className="form-group" style={{flex:1}}><label className="form-label">글자색</label><div style={{display:'flex',gap:6,alignItems:'center'}}><input type="color" value={bodyStyle.header_color} onChange={e=>setBodyStyle({...bodyStyle,header_color:e.target.value})} style={{width:32,height:28,border:'1px solid #d1d5db',borderRadius:4,padding:0,cursor:'pointer'}} /><input className="form-input" value={bodyStyle.header_color} onChange={e=>setBodyStyle({...bodyStyle,header_color:e.target.value})} style={{flex:1,fontSize:11}} /></div></div>
            <div className="form-group" style={{flex:1}}><label className="form-label">글자크기 (px)</label><input type="number" className="form-input" min={7} max={14} value={bodyStyle.header_font_size} onChange={e=>setBodyStyle({...bodyStyle,header_font_size:parseInt(e.target.value)||9})} /></div>
          </div>
          <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:8,paddingBottom:4,borderBottom:'1px solid #e2e8f0'}}>본문 (항목)</div>
          <div className="form-row" style={{marginBottom:8}}>
            <div className="form-group" style={{flex:1}}><label className="form-label">글자크기 (px)</label><input type="number" className="form-input" min={8} max={14} value={bodyStyle.body_font_size} onChange={e=>setBodyStyle({...bodyStyle,body_font_size:parseInt(e.target.value)||10})} /></div>
            <div className="form-group" style={{flex:1}}><label className="form-label">세부사항 (px)</label><input type="number" className="form-input" min={7} max={13} value={bodyStyle.detail_font_size} onChange={e=>setBodyStyle({...bodyStyle,detail_font_size:parseInt(e.target.value)||9})} /></div>
            <div className="form-group" style={{flex:1}}><label className="form-label">행간 (줄 간격)</label><input type="number" className="form-input" min={1} max={3} step={0.1} value={bodyStyle.body_line_height} onChange={e=>setBodyStyle({...bodyStyle,body_line_height:parseFloat(e.target.value)||1.8})} /></div>
          </div>
          <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:8,paddingBottom:4,borderBottom:'1px solid #e2e8f0'}}>합계 영역</div>
          <div className="form-row" style={{marginBottom:8}}>
            <div className="form-group" style={{flex:1}}><label className="form-label">합계행 배경색</label><div style={{display:'flex',gap:6,alignItems:'center'}}><input type="color" value={bodyStyle.total_bg} onChange={e=>setBodyStyle({...bodyStyle,total_bg:e.target.value})} style={{width:32,height:28,border:'1px solid #d1d5db',borderRadius:4,padding:0,cursor:'pointer'}} /><input className="form-input" value={bodyStyle.total_bg} onChange={e=>setBodyStyle({...bodyStyle,total_bg:e.target.value})} style={{flex:1,fontSize:11}} /></div></div>
            <div className="form-group" style={{flex:1}}><label className="form-label">합계 글자크기 (px)</label><input type="number" className="form-input" min={9} max={16} value={bodyStyle.total_font_size} onChange={e=>setBodyStyle({...bodyStyle,total_font_size:parseInt(e.target.value)||11})} /></div>
          </div>
          <div className="form-row" style={{marginBottom:12}}>
            <div className="form-group" style={{flex:1}}><label className="form-label">최종합계 배경색</label><div style={{display:'flex',gap:6,alignItems:'center'}}><input type="color" value={bodyStyle.final_bg} onChange={e=>setBodyStyle({...bodyStyle,final_bg:e.target.value})} style={{width:32,height:28,border:'1px solid #d1d5db',borderRadius:4,padding:0,cursor:'pointer'}} /><input className="form-input" value={bodyStyle.final_bg} onChange={e=>setBodyStyle({...bodyStyle,final_bg:e.target.value})} style={{flex:1,fontSize:11}} /></div></div>
            <div className="form-group" style={{flex:1}}><label className="form-label">최종합계 글자색</label><div style={{display:'flex',gap:6,alignItems:'center'}}><input type="color" value={bodyStyle.final_color} onChange={e=>setBodyStyle({...bodyStyle,final_color:e.target.value})} style={{width:32,height:28,border:'1px solid #d1d5db',borderRadius:4,padding:0,cursor:'pointer'}} /><input className="form-input" value={bodyStyle.final_color} onChange={e=>setBodyStyle({...bodyStyle,final_color:e.target.value})} style={{flex:1,fontSize:11}} /></div></div>
          </div>

          <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:8,paddingBottom:4,borderBottom:'1px solid #e2e8f0'}}>비고</div>
          <div className="form-row" style={{marginBottom:12}}>
            <div className="form-group" style={{flex:1}}><label className="form-label">비고 글씨크기 (px)</label><input type="number" className="form-input" min={7} max={14} value={bodyStyle.notes_font_size||10} onChange={e=>setBodyStyle({...bodyStyle,notes_font_size:parseInt(e.target.value)||10})} /></div>
          </div>

          <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:8,paddingBottom:4,borderBottom:'1px solid #e2e8f0'}}>셀 안쪽 여백 (px)</div>
          <div className="form-row" style={{marginBottom:12}}>
            <div className="form-group" style={{flex:1}}><label className="form-label">상</label><input type="number" className="form-input" min={0} max={20} value={bodyStyle.cell_padding_top} onChange={e=>setBodyStyle({...bodyStyle,cell_padding_top:parseInt(e.target.value)||0})} /></div>
            <div className="form-group" style={{flex:1}}><label className="form-label">하</label><input type="number" className="form-input" min={0} max={20} value={bodyStyle.cell_padding_bottom} onChange={e=>setBodyStyle({...bodyStyle,cell_padding_bottom:parseInt(e.target.value)||0})} /></div>
            <div className="form-group" style={{flex:1}}><label className="form-label">좌</label><input type="number" className="form-input" min={0} max={20} value={bodyStyle.cell_padding_left} onChange={e=>setBodyStyle({...bodyStyle,cell_padding_left:parseInt(e.target.value)||0})} /></div>
            <div className="form-group" style={{flex:1}}><label className="form-label">우</label><input type="number" className="form-input" min={0} max={20} value={bodyStyle.cell_padding_right} onChange={e=>setBodyStyle({...bodyStyle,cell_padding_right:parseInt(e.target.value)||0})} /></div>
          </div>
          <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:6,paddingBottom:4,borderBottom:'1px solid #e2e8f0'}}>컬럼 너비 (px)</div>
          <div style={{fontSize:11,color:'#94a3b8',marginBottom:8}}>모두 비우면 자동 레이아웃으로 작동합니다.</div>
          {(() => {
            const cw = bodyStyle.column_widths || {};
            const setCw = (key, val) => setBodyStyle({...bodyStyle, column_widths:{...cw, [key]: val}});
            const cols = [
              {key:'no', label:'No'},
              {key:'item', label:'품목'},
              {key:'detail', label:'세부사항'},
              {key:'supply_price', label:'공급가액'},
              {key:'qty', label:'수량'},
              {key:'amount', label:'공급금액'},
              {key:'consumer_price', label:'소비자단가'},
              {key:'consumer_amount', label:'소비자금액'},
            ];
            return (
              <div className="form-row" style={{flexWrap:'wrap',gap:8,marginBottom:12}}>
                {cols.map(({key,label}) => (
                  <div key={key} className="form-group" style={{flex:'0 0 calc(25% - 8px)',minWidth:90}}>
                    <label className="form-label" style={{fontSize:11}}>{label}</label>
                    <input type="number" className="form-input" min={0} max={400} placeholder="자동"
                      value={cw[key] || ''} onChange={e => setCw(key, e.target.value)} />
                  </div>
                ))}
              </div>
            );
          })()}
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary" disabled={bsSaving} onClick={async()=>{
              setBsSaving(true); setBsMsg('');
              try { await api.put('/settings/estimate_body_style',{value:bodyStyle}); setBsMsg('저장되었습니다.'); }
              catch(e) { setBsMsg(e.message); } finally { setBsSaving(false); }
            }}>{bsSaving?'저장 중...':'저장'}</button>
            <button className="btn btn-outline" onClick={()=>setBodyStyle({...BODY_STYLE_DEFAULTS})}>기본값 초기화</button>
          </div>
        </div>
        <div style={{flex:1,minWidth:300}}>
          <div style={{fontSize:12,fontWeight:700,color:'#475569',marginBottom:8}}>미리보기</div>
          <BodyStylePreview bodyStyle={bodyStyle} />
        </div>
      </div>
    </div>
  );
}

function EstimateSettingTab() {
  const [settings, setSettings] = useState({});
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [editWindowMin, setEditWindowMin] = useState('60');
  const [ewMsg, setEwMsg] = useState('');

  useEffect(() => {
    api.get('/settings').then(st => { setSettings(st); if (st.published_edit_window_min != null) setEditWindowMin(String(st.published_edit_window_min)); });
    api.get('/settings/tags/all').then(setTags);
  }, []);

  const saveFormat = async () => {
    setSaving(true); setMsg('');
    try { await api.put('/settings/estimate_number_format', { value: settings.estimate_number_format }); setMsg('저장되었습니다.'); }
    catch (e) { setMsg(e.message); } finally { setSaving(false); }
  };

  const addTag = async () => {
    if (!newTag) return;
    try { await api.post('/settings/tags', { name: newTag, color: 'gray' }); setNewTag(''); api.get('/settings/tags/all').then(setTags); }
    catch (e) { alert(e.message); }
  };

  const deleteTag = async (id) => {
    try { await api.del(`/settings/tags/${id}`); api.get('/settings/tags/all').then(setTags); }
    catch (e) { alert(e.message); }
  };

  const saveEditWindow = async () => {
    const n = Number(editWindowMin);
    if (!Number.isInteger(n) || n < 0 || n > 10080) { setEwMsg('0~10080 사이의 정수(분)를 입력하세요.'); return; }
    setEwMsg('');
    try { await api.put('/settings/published_edit_window_min', { value: n }); setEwMsg('저장되었습니다.'); }
    catch (e) { setEwMsg(e.message); }
  };

  const genPreview = () => {
    const fmt = settings.estimate_number_format || '';
    const now = new Date();
    const yy = String(now.getFullYear()).slice(2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return fmt.replace('{yyyy}', String(now.getFullYear())).replace('{yy}', yy).replace('{mm}', mm).replace('{dd}', dd).replace('{yymmdd}', `${yy}${mm}${dd}`).replace('{고유이니셜}', 'KCS').replace(/\{(0+)\}/, '0001');
  };

  return (
    <div>
      <div className="card">
        <div className="card-title mb-8">견적번호 양식</div>
        {msg && <div className="alert alert-info" style={{ marginBottom: 12 }}>{msg}</div>}
        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">양식 패턴</label>
            <input className="form-input" value={settings.estimate_number_format || ''} onChange={e => setSettings({ ...settings, estimate_number_format: e.target.value })} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">미리보기</label>
            <input className="form-input" value={genPreview()} readOnly style={{ background: '#f8fafc' }} />
          </div>
        </div>
        <div className="text-muted mb-8">사용 가능: {'{yyyy}'}, {'{yy}'}, {'{mm}'}, {'{dd}'}, {'{yymmdd}'}, {'{고유이니셜}'}, {'{0000}'}</div>
        <button className="btn btn-primary" onClick={saveFormat} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </div>

      <div className="card">
        <div className="card-title mb-8">발행 후 수정 가능 시간</div>
        {ewMsg && <div className="alert alert-info" style={{ marginBottom: 12 }}>{ewMsg}</div>}
        <div className="form-group" style={{ maxWidth: 240 }}>
          <label className="form-label">수정 가능 시간 (분)</label>
          <input className="form-input" type="number" min="0" max="10080" step="1" value={editWindowMin} onChange={e => setEditWindowMin(e.target.value)} />
        </div>
        <div className="text-muted mb-8">견적서를 발행한 뒤 이 시간 안에는 새 버전을 만들지 않고 작성자가 직접 수정할 수 있습니다. 기본값 60분, 0이면 발행 후 수정할 수 없습니다. 이미 발행된 견적서에도 바로 적용됩니다.</div>
        <button className="btn btn-primary" onClick={saveEditWindow}>저장</button>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">태그 관리</div></div>
        <div style={{overflowX:'auto'}}><table>
          <thead><tr><th>태그명</th><th>유형</th><th>작업</th></tr></thead>
          <tbody>
            {tags.map(t => (
              <tr key={t.id}>
                <td><span className={`tag tag-${t.color || 'gray'}`}>{t.name}</span></td>
                <td>{t.is_predefined ? '사전정의' : '커스텀'}</td>
                <td>{!t.is_predefined && <button className="btn btn-danger btn-sm" onClick={() => deleteTag(t.id)}>삭제</button>}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <input className="form-input" style={{ width: 200 }} value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="새 태그명" />
          <button className="btn btn-primary btn-sm" onClick={addTag}>추가</button>
        </div>
      </div>

    </div>
  );
}

/* ===== 4. 사용자 그룹/권한 ===== */
// 권한 모달 메뉴 카드 — 사이드바 14개 항목과 1:1 매칭. `menu` 는 permission 키.
// 액션 키 원칙: 메뉴 접근=열람, 생성·수정·발행=한 키로 통합, 삭제=분리.
const MENU_GROUPS = [
  { menu: '대시보드',          icon: '🖥️', actions: [] },
  { menu: '제품 견적서',       icon: '📦', actions: ['제품 견적서 생성·수정', '제품 견적서 삭제'] },
  { menu: '서비스견적서',      icon: '🧑‍💻', actions: ['서비스견적서 생성·수정', '서비스견적서 삭제'] },
  { menu: '승인상태',          icon: '🙏', actions: ['승인 처리'] },
  { menu: '제품 묶음',         icon: '🔗', actions: ['제품 묶음 생성·수정', '제품 묶음 삭제'] },
  { menu: '상품 관리',         icon: '🎁', actions: ['상품 생성·수정', '상품 삭제'] },
  { menu: '자재 관리',         icon: '🧰', actions: ['자재 생성·수정', '자재 삭제', '원가기준표 관리', '원가기준표 삭제'] },
  { menu: '서비스 관리',       icon: '⛓️', actions: ['서비스 생성·수정', '서비스 삭제'] },
  { menu: '직무별 가격 관리',  icon: '🏷️', actions: ['직무·등급 생성·수정', '직무·등급 삭제'] },
  { menu: '고객 관리',         icon: '📇', actions: ['고객 생성·수정', '고객 삭제'] },
  { menu: '조직 관리',         icon: '🏢', actions: ['조직 생성·수정', '조직 삭제'] },
  { menu: '사용자관리',        icon: '👩🏻‍💼', actions: ['사용자 생성·수정', '사용자 삭제'] },
  { menu: '감사 로그',         icon: '📜', actions: [] },
  { menu: '설정',              icon: '⚙️', actions: ['시스템 설정 변경'] },
];
const ALL_MENUS = MENU_GROUPS.map(g => g.menu);
const ALL_ACTIONS = MENU_GROUPS.flatMap(g => g.actions);

function GroupsTab() {
  const [groups, setGroups] = useState([]);
  const [showPerm, setShowPerm] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(null);
  const [newName, setNewName] = useState('');
  const [editName, setEditName] = useState('');
  const [menuPerms, setMenuPerms] = useState({});
  const [actionPerms, setActionPerms] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());

  const menuItems = ALL_MENUS;
  const actionItems = ALL_ACTIONS;

  const toggleExpand = (menu) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(menu)) next.delete(menu); else next.add(menu);
    return next;
  });

  useEffect(() => { load(); }, []);
  const load = () => api.get('/groups').then(setGroups).catch(console.error);

  const isAllPerms = (perms) => perms && perms.all === true;

  const openPerm = (g) => {
    setShowPerm(g);
    setExpanded(new Set()); // 매번 닫힌 상태로 진입
    if (isAllPerms(g.menu_permissions)) {
      const all = {}; menuItems.forEach(m => { all[m] = true; }); setMenuPerms(all);
    } else {
      // 저장값에 남은 구 키를 신규 키로 흡수 — 모달 체크 상태에 즉시 반영
      setMenuPerms(normalizePerms(g.menu_permissions, LEGACY_MENU_ALIASES_MAP, menuItems) || {});
    }
    if (isAllPerms(g.action_permissions)) {
      const all = {}; actionItems.forEach(a => { all[a] = true; }); setActionPerms(all);
    } else {
      setActionPerms(normalizePerms(g.action_permissions, LEGACY_ACTION_ALIASES_MAP, actionItems) || {});
    }
  };

  const savePerm = async () => {
    const allMenuChecked = menuItems.every(m => menuPerms[m]);
    const allActionChecked = actionItems.every(a => actionPerms[a]);
    const menuToSave = allMenuChecked ? { all: true } : menuPerms;
    const actionToSave = allActionChecked ? { all: true } : actionPerms;
    try { await api.put(`/groups/${showPerm.id}`, { menu_permissions: menuToSave, action_permissions: actionToSave }); setShowPerm(null); load(); }
    catch (e) { alert(e.message); }
  };

  const addGroup = async () => {
    if (!newName) return;
    try { await api.post('/groups', { name: newName }); setNewName(''); setShowAdd(false); load(); }
    catch (e) { alert(e.message); }
  };

  const openEditGroup = (g) => { setShowEditGroup(g); setEditName(g.name); };

  const saveEditGroup = async () => {
    if (!editName) return;
    try { await api.put(`/groups/${showEditGroup.id}`, { name: editName }); setShowEditGroup(null); load(); }
    catch (e) { alert(e.message); }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm('이 그룹을 삭제하시겠습니까? 소속 사용자가 있으면 삭제할 수 없습니다.')) return;
    try { await api.del(`/groups/${id}`); load(); } catch (e) { alert(e.message); }
  };

  return (
    <div>
      <div className="card">
        <div className="card-header"><div className="card-title">그룹 관리</div><button className="btn btn-primary btn-sm" onClick={() => { setShowAdd(true); setNewName(''); }}>+ 그룹 추가</button></div>
        <div style={{overflowX:'auto'}}><table>
          <thead><tr><th>그룹명</th><th>소속인원</th><th>작업</th></tr></thead>
          <tbody>
            {groups.filter(g => g.name !== '시스템관리자').map(g => (
              <tr key={g.id}>
                <td className="fw-bold">{g.name}</td>
                <td>{g.member_count}명</td>
                <td><div className="btn-group">
                  <button className="btn btn-outline btn-sm" onClick={() => openEditGroup(g)}>편집</button>
                  <button className="btn btn-outline btn-sm" onClick={() => openPerm(g)}>권한설정</button>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteGroup(g.id)}>삭제</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {/* 그룹 추가 모달 */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 360 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>그룹 추가</div>
            <div className="form-group"><label className="form-label">그룹명</label><input className="form-input" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addGroup(); }} autoFocus /></div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowAdd(false)}>취소</button>
              <button className="btn btn-primary" onClick={addGroup}>추가</button>
            </div>
          </div>
        </div>
      )}

      {/* 그룹 편집 모달 */}
      {showEditGroup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 360 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
              <span>그룹 편집</span>
              <span style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowEditGroup(null)}>✕</span>
            </div>
            <div className="form-group">
              <label className="form-label">그룹명</label>
              <input className="form-input" value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveEditGroup(); }} autoFocus />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowEditGroup(null)}>취소</button>
              <button className="btn btn-primary" onClick={saveEditGroup}>저장</button>
            </div>
          </div>
        </div>
      )}

      {showPerm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 560, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>권한 설정: {showPerm.name}</span>
              <span style={{ cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowPerm(null)}>✕</span>
            </div>
            {showPerm.name === '시스템관리자' && (
              <div className="alert alert-info" style={{ marginBottom: 12 }}>ℹ 시스템관리자 그룹은 모든 메뉴 및 권한이 항상 활성화됩니다.</div>
            )}
            <div style={{ display:'flex', gap:6, marginBottom:12 }}>
              <button className="btn btn-outline btn-sm" onClick={() => {
                const mAll = {}; ALL_MENUS.forEach(m => { mAll[m] = true; });
                const aAll = {}; ALL_ACTIONS.forEach(a => { aAll[a] = true; });
                setMenuPerms(mAll); setActionPerms(aAll);
              }}>전체</button>
              <button className="btn btn-outline btn-sm" onClick={() => { setMenuPerms({}); setActionPerms({}); }}>모두 해제</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {MENU_GROUPS.map(g => {
                const hasActions = g.actions.length > 0;
                const isOpen = expanded.has(g.menu);
                const menuOn = !!menuPerms[g.menu];
                return (
                  <div key={g.menu} style={{ border:'1px solid #e2e8f0', borderRadius:8, overflow:'hidden' }}>
                    <div
                      onClick={() => hasActions && toggleExpand(g.menu)}
                      style={{
                        display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
                        cursor: hasActions ? 'pointer' : 'default',
                        background: isOpen ? '#f8fafc' : '#fff',
                      }}>
                      <span style={{ width:14, color:'#94a3b8', fontSize:11 }}>{hasActions ? (isOpen ? '▼' : '▶') : ''}</span>
                      <input type="checkbox" checked={menuOn} onClick={e => e.stopPropagation()}
                        onChange={e => setMenuPerms({ ...menuPerms, [g.menu]: e.target.checked })} />
                      <span style={{ fontSize:13, width:20, textAlign:'center' }}>{g.icon || ''}</span>
                      <span style={{ fontSize:13, fontWeight:600, flex:1 }}>{g.menu}</span>
                      {hasActions
                        ? <span style={{ fontSize:11, color:'#94a3b8' }}>· {g.actions.length} 액션</span>
                        : <span style={{ fontSize:11, color:'#cbd5e1' }}>메뉴만</span>}
                    </div>
                    {hasActions && isOpen && (
                      <div style={{ padding:'8px 12px 10px 40px', borderTop:'1px solid #f1f5f9', background:'#fafbfc' }}>
                        {g.actions.map(a => (
                          <label key={a} style={{
                            display:'flex', alignItems:'center', gap:8, fontSize:12, marginBottom:5,
                            opacity: menuOn ? 1 : 0.4, cursor: menuOn ? 'pointer' : 'not-allowed',
                          }}>
                            <input type="checkbox" disabled={!menuOn} checked={!!actionPerms[a]}
                              onChange={e => setActionPerms({ ...actionPerms, [a]: e.target.checked })} /> {a}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-outline" onClick={() => setShowPerm(null)}>취소</button>
              <button className="btn btn-primary" onClick={savePerm}>저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== 5. 부품/제품 설정 ===== */
function CodeCategory({ category, label }) {
  const [codes, setCodes] = useState([]);
  const [addValue, setAddValue] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => { load(); }, []);
  const load = () => api.get(`/settings/codes/${category}`).then(setCodes).catch(console.error);

  const handleAdd = async () => {
    if (!addValue.trim()) return;
    setError('');
    try {
      await api.post('/settings/codes', { category, code: addValue.toLowerCase().replace(/\s/g, '_'), label: addValue.trim() });
      setAddValue(''); load();
    } catch (e) { setError(e.message); }
  };

  const handleEdit = async (id) => {
    if (!editValue.trim()) return;
    setError('');
    try { await api.put(`/settings/codes/${id}`, { label: editValue.trim() }); setEditingId(null); setEditValue(''); load(); }
    catch (e) { setError(e.message); }
  };

  // 상품구분 전용 옵션: 수량입력 강제 (켜면 이 상품구분의 상품은 견적 발행 전에 수량을 직접 입력해야 함)
  const handleToggleRequireQty = async (c) => {
    setError('');
    try { await api.put(`/settings/codes/${c.id}`, { require_quantity: !c.require_quantity }); load(); }
    catch (e) { setError(e.message); }
  };

  const handleDelete = async (id) => {
    setError('');
    try { await api.del(`/settings/codes/${id}`); load(); }
    catch (e) { setError(e.message); }
  };

  // Drag & drop
  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    // 드래그 중 + 아이콘 제거: 해당 요소를 dragImage로 사용
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    }
  };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIdx === null || dragIdx === idx) return;
    const reordered = [...codes];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setCodes(reordered);
    setDragIdx(idx);
  };
  const handleDragEnd = async () => {
    setDragIdx(null);
    const items = codes.map((c, i) => ({ id: c.id, sort_order: i }));
    try { await api.put('/settings/codes/reorder', { items }); }
    catch (e) { console.error(e); load(); }
  };

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="card-title mb-8">{label} 관리</div>
      {error && <div className="alert alert-danger" style={{ marginBottom: 8, fontSize: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input className="form-input" style={{ flex: 1 }} value={addValue} onChange={e => setAddValue(e.target.value)}
          placeholder="추가할 항목명" onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} />
        <button className="btn btn-primary btn-sm" onClick={handleAdd}>추가</button>
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: 8 }}>
        {codes.map((c, idx) => (
          <div
            key={c.id}
            draggable={editingId !== c.id}
            onDragStart={e => handleDragStart(e, idx)}
            onDragOver={e => handleDragOver(e, idx)}
            onDragEnd={handleDragEnd}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 12px', borderBottom: '1px solid #f1f5f9',
              background: dragIdx === idx ? '#eff6ff' : '#fff',
              cursor: editingId === c.id ? 'default' : 'grab',
            }}
          >
            <span style={{ color: '#cbd5e1', fontSize: 12, cursor: 'grab', flexShrink: 0 }}>☰</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              {editingId === c.id ? (
                <input className="form-input" style={{ padding: '3px 8px', fontSize: 13 }} value={editValue} onChange={e => setEditValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleEdit(c.id); if (e.key === 'Escape') { setEditingId(null); setEditValue(''); } }} autoFocus />
              ) : (
                <span style={{ fontSize: 13 }}>{c.label}</span>
              )}
            </div>
            {editingId === c.id ? (
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <button className="btn btn-primary btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => handleEdit(c.id)}>저장</button>
                <button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => { setEditingId(null); setEditValue(''); }}>취소</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
                {category === 'product_category' && (
                  <label title="켜면 이 상품구분의 상품(또는 같은 이름의 상품 유형: Hardware, License, Appliance, Software)은 견적서에서 수량 칸이 빈 칸으로 시작하고, 수량을 입력해야 발행할 수 있습니다 (임시저장은 비어 있어도 됨)"
                    style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: c.require_quantity ? '#b45309' : '#94a3b8', fontWeight: c.require_quantity ? 700 : 400, cursor: 'pointer', marginRight: 6, whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={!!c.require_quantity} onChange={() => handleToggleRequireQty(c)} />
                    수량입력 강제
                  </label>
                )}
                <button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => { setEditingId(c.id); setEditValue(c.label); setError(''); }}>편집</button>
                <button className="btn btn-danger btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => handleDelete(c.id)}>삭제</button>
              </div>
            )}
          </div>
        ))}
        {codes.length === 0 && <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 13 }}>등록된 항목이 없습니다.</div>}
      </div>
    </div>
  );
}

function PartProductTab() {
  return (
    <div className="grid-2">
      <CodeCategory category="part_type" label="부품종류" />
      <CodeCategory category="unit" label="단위" />
      <CodeCategory category="product_category" label="상품구분" />
      <CodeCategory category="product_status" label="상품상태" />
    </div>
  );
}

/* ===== 6. UID 권한 ===== */
function UidTab() {
  const [delegations, setDelegations] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ target_uid: '', uid_type: 'personal', original_owner: '', delegate_type: 'user', delegate_user_id: '', delegate_group_id: '' });
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => { load(); api.get('/users').then(setUsers); api.get('/groups').then(setGroups); }, []);
  const load = () => api.get('/settings/uid-delegations/all').then(setDelegations).catch(console.error);

  const handleAdd = async () => {
    try {
      await api.post('/settings/uid-delegations', form);
      setShowAdd(false); setForm({ target_uid: '', uid_type: 'personal', original_owner: '', delegate_type: 'user', delegate_user_id: '', delegate_group_id: '' }); load();
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async (id) => { await api.del(`/settings/uid-delegations/${id}`); load(); };

  return (
    <div className="card">
      <div className="card-header"><div className="card-title">UID 권한 관리</div><button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ 권한 추가</button></div>
      <div style={{overflowX:'auto'}}><table>
        <thead><tr><th>대상 UID</th><th>유형</th><th>원래 소유</th><th>위임 대상</th><th>위임 유형</th><th>작업</th></tr></thead>
        <tbody>
          {delegations.map(d => (
            <tr key={d.id}>
              <td>{d.target_uid}</td>
              <td><span className={`badge ${d.uid_type === 'personal' ? 'badge-blue' : 'badge-teal'}`}>{d.uid_type === 'personal' ? '개인' : '부서'}</span></td>
              <td>{d.original_owner}</td>
              <td>{d.delegate_user_name || d.delegate_group_name}</td>
              <td>{d.delegate_type === 'user' ? '사용자' : '그룹'}</td>
              <td><button className="btn btn-danger btn-sm" onClick={() => handleDelete(d.id)}>해제</button></td>
            </tr>
          ))}
          {delegations.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>위임된 권한이 없습니다.</td></tr>}
        </tbody>
      </table></div>

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 460 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #e2e8f0' }}>UID 권한 추가</div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">대상 UID</label><input className="form-input" value={form.target_uid} onChange={e => setForm({ ...form, target_uid: e.target.value })} placeholder="U-0042 또는 D-0010" /></div>
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">UID 유형</label>
                <select className="form-input" value={form.uid_type} onChange={e => setForm({ ...form, uid_type: e.target.value })}><option value="personal">개인</option><option value="department">부서</option></select>
              </div>
            </div>
            <div className="form-group"><label className="form-label">원래 소유자</label><input className="form-input" value={form.original_owner} onChange={e => setForm({ ...form, original_owner: e.target.value })} placeholder="예: 김철수 (퇴사)" /></div>
            <div className="form-group"><label className="form-label">위임 유형</label>
              <select className="form-input" value={form.delegate_type} onChange={e => setForm({ ...form, delegate_type: e.target.value })}>
                <option value="user">사용자</option><option value="group">그룹</option>
              </select>
            </div>
            {form.delegate_type === 'user' && (
              <div className="form-group"><label className="form-label">위임 대상 사용자</label>
                <select className="form-input" value={form.delegate_user_id} onChange={e => setForm({ ...form, delegate_user_id: e.target.value })}>
                  <option value="">선택</option>
                  {users.map(u => <option key={u.uid} value={u.id}>{u.name} ({u.uid})</option>)}
                </select>
              </div>
            )}
            {form.delegate_type === 'group' && (
              <div className="form-group"><label className="form-label">위임 대상 그룹</label>
                <select className="form-input" value={form.delegate_group_id} onChange={e => setForm({ ...form, delegate_group_id: e.target.value })}>
                  <option value="">선택</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowAdd(false)}>취소</button>
              <button className="btn btn-primary" onClick={handleAdd}>추가</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== 7. 백업/복구 ===== */
function BackupTab() {
  const [settings, setSettings] = useState({});
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [uploadRestoring, setUploadRestoring] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success, message }
  const [connectionVerified, setConnectionVerified] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { loadSettings(); loadHistory(); }, []);

  // 방식 변경 시 테스트 결과 초기화
  useEffect(() => { setTestResult(null); setConnectionVerified(false); }, [settings.method, settings.remote_host, settings.remote_port, settings.remote_user, settings.remote_password, settings.remote_path]);

  const loadSettings = async () => {
    try { const data = await api.get('/backup/settings'); setSettings(data || {}); if (data?.method === 'local') setConnectionVerified(true); } catch (e) { console.error(e); }
  };

  const loadHistory = async () => {
    try { setHistory(await api.get('/backup/history')); } catch (e) { console.error(e); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const result = await api.post('/backup/test-connection', {
        method: settings.method,
        remote_host: settings.remote_host,
        remote_port: settings.remote_port,
        remote_user: settings.remote_user,
        remote_password: settings.remote_password,
        remote_path: settings.remote_path,
      });
      setTestResult({ success: true, message: result.message });
      setConnectionVerified(true);
    } catch (e) {
      setTestResult({ success: false, message: e.message });
      setConnectionVerified(false);
    } finally { setTesting(false); }
  };

  const isRemote = settings.method === 'ftp' || settings.method === 'sftp';
  const canSave = !isRemote || connectionVerified;

  const saveSettings = async () => {
    if (isRemote && !connectionVerified) {
      alert('FTP/SFTP 접속 테스트를 먼저 수행해 주세요.');
      return;
    }
    setSaving(true);
    try {
      await api.put('/backup/settings', { value: settings });
      await loadHistory();
      alert('백업 설정이 저장되었습니다.');
    }
    catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  const runBackup = async () => {
    if (!window.confirm('지금 백업을 실행하시겠습니까?')) return;
    setRunning(true);
    try {
      const result = await api.post('/backup/run');
      alert(`백업 완료: ${result.filename} (${formatSize(result.fileSize)})`);
      loadHistory();
    } catch (e) { alert(e.message); }
    finally { setRunning(false); }
  };

  const handleRestore = async (name) => {
    if (!window.confirm('이 백업으로 복원하시겠습니까?\n\n⚠ 현재 데이터가 백업 시점으로 되돌아갑니다.\n복원 전 현재 상태의 백업이 자동 생성됩니다.')) return;
    setRestoring(name);
    try {
      const result = await api.post(`/backup/files/${encodeURIComponent(name)}/restore`);
      alert(result.message);
      loadHistory();
    } catch (e) { alert(e.message); }
    finally { setRestoring(null); }
  };

  const handleUploadRestore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!window.confirm(`'${file.name}' 파일로 복원하시겠습니까?\n\n⚠ 현재 데이터가 업로드한 백업 시점으로 덮어쓰여집니다.\n복원 전 현재 상태의 백업이 자동 생성됩니다.`)) return;
    setUploadRestoring(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_ORIGIN}/api/backup/restore-upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '복원 실패');
      alert(data.message || '복원이 완료되었습니다.');
      loadHistory();
    } catch (err) {
      alert(err.message);
    } finally { setUploadRestoring(false); }
  };

  const handleDelete = async (name) => {
    if (!window.confirm('이 백업을 삭제하시겠습니까? 백업 파일도 함께 삭제됩니다.')) return;
    try { await api.del(`/backup/files/${encodeURIComponent(name)}`); loadHistory(); }
    catch (e) { alert(e.message); }
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const f = (key) => ({ value: settings[key] || '', onChange: e => setSettings({ ...settings, [key]: e.target.value }) });

  return (
    <div>
      <div className="card">
        <div className="card-title mb-8">백업 설정</div>
        <div className="grid-2">
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>백업 경로</div>
            <div className="form-group"><label className="form-label">저장 방식</label>
              <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                {['local', 'ftp', 'sftp'].map(m => <label key={m}><input type="radio" name="bk" checked={settings.method === m} onChange={() => setSettings({ ...settings, method: m })} /> {m.toUpperCase()}</label>)}
              </div>
            </div>
            {settings.method === 'local' && (
              <div className="form-group"><label className="form-label">로컬 경로</label><input className="form-input" {...f('path')} placeholder="/backup/soosan-erp" /></div>
            )}
            {(settings.method === 'ftp' || settings.method === 'sftp') && (
              <>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}><label className="form-label">호스트</label><input className="form-input" {...f('remote_host')} placeholder="ftp.soosan.co.kr" /></div>
                  <div className="form-group" style={{ flex: 1 }}><label className="form-label">포트</label><input className="form-input" {...f('remote_port')} placeholder={settings.method === 'ftp' ? '21' : '22'} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}><label className="form-label">계정</label><input className="form-input" {...f('remote_user')} placeholder="backup_user" /></div>
                  <div className="form-group" style={{ flex: 1 }}><label className="form-label">비밀번호</label><input className="form-input" type="password" {...f('remote_password')} /></div>
                </div>
                <div className="form-group"><label className="form-label">원격 경로</label><input className="form-input" {...f('remote_path')} placeholder="/backup/soosan-erp" /></div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                  <button className="btn btn-outline btn-sm" onClick={testConnection} disabled={testing || !settings.remote_host || !settings.remote_user || !settings.remote_path}>
                    {testing ? '테스트 중...' : '접속 테스트'}
                  </button>
                  {testResult && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: testResult.success ? '#22c55e' : '#ef4444' }}>
                      {testResult.success ? '✓' : '✕'} {testResult.message}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>자동 백업 스케줄</div>
            <div className="form-group"><label className="form-label">자동 백업</label>
              <label style={{ fontSize: 13 }}><input type="checkbox" checked={settings.auto || false} onChange={e => setSettings({ ...settings, auto: e.target.checked })} /> 활성화</label>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">주기</label>
                <select className="form-input" value={settings.schedule || 'daily'} onChange={e => setSettings({ ...settings, schedule: e.target.value })}>
                  <option value="daily">매일</option><option value="weekly">매주</option><option value="monthly">매월</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">시간</label><input className="form-input" type="time" {...f('time')} /></div>
              <div className="form-group" style={{ flex: 1 }}><label className="form-label">보관기간</label>
                <select className="form-input" value={settings.retention_days || 30} onChange={e => setSettings({ ...settings, retention_days: parseInt(e.target.value) })}>
                  <option value={7}>7일</option><option value={30}>30일</option><option value={90}>90일</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <button className="btn btn-primary" onClick={saveSettings} disabled={saving || !canSave} style={{ opacity: canSave ? 1 : 0.5 }}>
            {saving ? '저장 중...' : '설정 저장'}
          </button>
          {isRemote && !connectionVerified && <span style={{ fontSize: 12, color: '#f59e0b' }}>⚠ 접속 테스트를 먼저 수행해 주세요</span>}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">백업 이력</div>
          <div style={{display:'flex',gap:8}}>
            <input ref={fileInputRef} type="file" accept=".sql,.dump,.backup" style={{display:'none'}} onChange={handleUploadRestore} />
            <button className="btn btn-outline btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploadRestoring}>
              {uploadRestoring ? '복원 중...' : '파일 업로드 복원'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={runBackup} disabled={running}>{running ? '백업 중...' : '지금 백업 실행'}</button>
          </div>
        </div>
        {history.length > 0 ? (
          <div style={{overflowX:'auto'}}><table>
            <thead><tr><th>일시</th><th>유형</th><th>파일</th><th>크기</th><th>작업</th></tr></thead>
            <tbody>
              {history.map(h => {
                const typeLabel = { manual: '수동', auto: '자동', 'before-restore': '복원전', other: '기타' }[h.type] || '기타';
                const typeBadge = h.type === 'auto' ? 'badge-blue' : h.type === 'before-restore' ? 'badge-purple' : h.type === 'manual' ? 'badge-gray' : 'badge-gray';
                return (
                  <tr key={h.name}>
                    <td>{h.created_at ? new Date(h.created_at).toLocaleString('ko') : '-'}</td>
                    <td><span className={`badge ${typeBadge}`}>{typeLabel}</span></td>
                    <td style={{ fontSize: 11, fontFamily: 'monospace', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.name}
                    </td>
                    <td>{formatSize(parseInt(h.size))}</td>
                    <td>
                      <div className="btn-group">
                        <button className="btn btn-outline btn-sm" onClick={() => handleRestore(h.name)} disabled={restoring === h.name}>
                          {restoring === h.name ? '복원 중...' : '복원'}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(h.name)}>삭제</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        ) : (
          <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>백업 파일이 없습니다.</div>
        )}
        <div className="text-muted" style={{ marginTop: 8 }}>ℹ 복원 시 현재 데이터가 백업 시점으로 되돌아갑니다. 복원 전 현재 상태의 백업이 자동 생성됩니다.</div>
      </div>
    </div>
  );
}

/* ===== 8. 이메일 알림 ===== */
function EmailTab() {
  const [smtp, setSmtp] = useState({});
  const [notifs, setNotifs] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, message }
  const [testRecipient, setTestRecipient] = useState('');
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);

  // 알림 템플릿
  const [tplData, setTplData] = useState(null);  // { saved, defaults, vars, commonVars, labels }
  const [tplCategory, setTplCategory] = useState('approval_request');
  const [tplEdited, setTplEdited] = useState({}); // 카테고리별 { subject, body } 편집중 상태
  const [tplSaving, setTplSaving] = useState(false);
  const tplBodyRef = useRef(null);

  // 지정 그룹 수신자 (현재 price_set_change 만)
  const [groups, setGroups] = useState([]);  // [{id,name}]
  const [recipients, setRecipients] = useState({});  // {price_set_change: {group_ids:[..]}}

  const loadRecipients = async () => {
    try {
      const r = await api.get('/settings/notification-recipients');
      setGroups(r.groups || []);
      setRecipients(r.saved || {});
    } catch (e) { console.error(e); }
  };
  const togglePriceSetGroup = (gid) => {
    const cur = recipients.price_set_change?.group_ids || [];
    const next = cur.includes(gid) ? cur.filter(x => x !== gid) : [...cur, gid];
    setRecipients({ ...recipients, price_set_change: { group_ids: next } });
  };

  const loadTemplates = async () => {
    try {
      const r = await api.get('/settings/email-templates');
      setTplData(r);
      // saved 우선, 없으면 defaults 로 초기 편집 상태 채움
      const init = {};
      for (const cat of Object.keys(r.labels || {})) {
        if (cat === 'test') continue;
        const sv = (r.saved && r.saved[cat]) || {};
        const def = r.defaults[cat] || { subject: '', body: '' };
        init[cat] = {
          subject: sv.subject || def.subject || '',
          body: sv.body || def.body || '',
        };
      }
      setTplEdited(init);
    } catch (e) { console.error(e); }
  };

  const insertVarAtCursor = (varName) => {
    const ta = tplBodyRef.current;
    if (!ta) return;
    const before = tplEdited[tplCategory].body.slice(0, ta.selectionStart);
    const after  = tplEdited[tplCategory].body.slice(ta.selectionEnd);
    const insert = `{{${varName}}}`;
    const next = before + insert + after;
    setTplEdited({ ...tplEdited, [tplCategory]: { ...tplEdited[tplCategory], body: next } });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(before.length + insert.length, before.length + insert.length); }, 0);
  };

  const resetCategoryToDefault = () => {
    if (!tplData || !window.confirm(`${tplData.labels[tplCategory]} 템플릿을 기본값으로 되돌립니다.`)) return;
    const def = tplData.defaults[tplCategory] || { subject: '', body: '' };
    setTplEdited({ ...tplEdited, [tplCategory]: { subject: def.subject, body: def.body } });
  };

  const saveTemplates = async () => {
    setTplSaving(true);
    try {
      // 기본값과 동일한 카테고리는 저장에서 제외 (덜 어지러운 DB)
      const toSave = {};
      for (const [cat, t] of Object.entries(tplEdited)) {
        const def = tplData.defaults[cat] || { subject:'', body:'' };
        if (t.subject !== def.subject || t.body !== def.body) toSave[cat] = t;
      }
      await api.put('/settings/email_templates', { value: toSave });
      alert('템플릿이 저장되었습니다.');
      await loadTemplates();
    } catch (e) { alert(e.message); }
    finally { setTplSaving(false); }
  };

  // 미리보기 — 백엔드 동일 알고리즘 (블록 + 변수 치환)
  const renderPreview = (str) => {
    if (!str || !tplData) return '';
    const sampleByCat = {
      approval_request:  { 수신자명:'테스트승인자', 견적번호:'EST-TEST-001', 사업명:'테스트 사업', 수신처:'테스트 고객사', 요청자명:'홍길동', 승인사유:'예외 단가 적용', 공헌이익율:'12.3', 승인단계:'1차 결재' },
      approval_result:   { 수신자명:'홍길동', 견적번호:'EST-TEST-001', 사업명:'테스트 사업', 요청자명:'홍길동', 처리자명:'김부장', 처리결과:'승인 완료', 발행번호:'2026-001' },
      estimate_shared:   { 수신자명:'테스트수신자', 견적번호:'EST-TEST-002', 사업명:'테스트 사업', 수신처:'테스트 고객사', 공유자명:'박팀장', 공유메시지:'검토 부탁드립니다', 권한:'열람' },
      result_reminder:   { 수신자명:'홍길동', 견적번호:'EST-TEST-003', 사업명:'테스트 사업', 수신처:'테스트 고객사', 발행일:'2026-05-01', 경과일:'24', 작성자명:'홍길동' },
      price_set_change:  { 수신자명:'관리자', 버전:'v17', 메모:'2026년 5월 정기 업데이트', 변경자:'김자재', 변경시각:'2026-05-25 13:00' },
      backup_failure:    { 수신자명:'관리자', 오류메시지:'disk full', 발생시각:'2026-05-25 03:00:00' },
    };
    const now = new Date(); const pad = n=>String(n).padStart(2,'0');
    const vars = {
      현재일시: `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
      사이트명: 'SES 견적시스템',
      ...(sampleByCat[tplCategory] || {}),
    };
    let out = String(str);
    out = out.replace(/\{\{#(\w+|[가-힣]+)\}\}([\s\S]*?)\{\{\/\1\}\}/g, (_, key, inner) => {
      const v = vars[key];
      return (v !== undefined && v !== null && String(v).trim() !== '') ? inner : '';
    });
    out = out.replace(/\{\{(\w+|[가-힣]+)\}\}/g, (_, key) => {
      const v = vars[key];
      return (v === undefined || v === null) ? '' : String(v);
    });
    return out;
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try { const r = await api.get('/settings/email-logs?limit=100'); setLogs(r.rows || []); setLogsTotal(r.total || 0); }
    catch (e) { setLogs([]); setLogsTotal(0); }
    finally { setLogsLoading(false); }
  };

  useEffect(() => {
    api.get('/settings').then(s => {
      if (s.smtp) setSmtp(typeof s.smtp === 'string' ? JSON.parse(s.smtp) : s.smtp);
      if (s.email_notifications) setNotifs(typeof s.email_notifications === 'string' ? JSON.parse(s.email_notifications) : s.email_notifications);
    });
    loadLogs();
    loadTemplates();
    loadRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const testSmtp = async (withSend) => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post('/settings/smtp/test', { ...smtp, to: withSend ? testRecipient.trim() : undefined });
      setTestResult({ ok: true, message: r.message || '성공' });
    } catch (e) {
      setTestResult({ ok: false, message: e.message || String(e) });
    } finally { setTesting(false); if (withSend) loadLogs(); }
  };

  const save = async () => {
    if (!testResult?.ok) {
      if (!window.confirm('SMTP 연결 테스트가 통과되지 않았습니다. 그대로 저장할까요?')) return;
    }
    setSaving(true);
    try {
      await api.put('/settings/smtp', { value: smtp });
      await api.put('/settings/email_notifications', { value: notifs });
      await api.put('/settings/email_notification_recipients', { value: recipients });
      alert('저장되었습니다.');
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  const sf = (key) => ({ value: smtp[key] || '', onChange: e => setSmtp({ ...smtp, [key]: e.target.value }) });

  const notifItems = [
    { key: 'approval_request', label: '승인요청 접수', target: '승인권자' },
    { key: 'approval_result', label: '승인완료/반려', target: '요청자' },
    { key: 'estimate_shared', label: '견적서 공유받음', target: '대상자' },
    { key: 'result_reminder', label: '견적결과 미입력 리마인더', target: '작성자' },
    { key: 'price_set_change', label: '원가기준표 변경', target: '지정 그룹' },
    { key: 'backup_failure', label: '백업 실패', target: '시스템관리자' },
  ];

  return (
    <div className="card">
      <div className="card-title mb-8">이메일 알림 (SMTP)</div>
      <div className="grid-2">
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>SMTP 설정</div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}><label className="form-label">서버</label><input className="form-input" {...sf('host')} /></div>
            <div className="form-group" style={{ flex: 1 }}><label className="form-label">포트</label><input className="form-input" {...sf('port')} /></div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}><label className="form-label">계정</label><input className="form-input" {...sf('user')} /></div>
            <div className="form-group" style={{ flex: 1 }}><label className="form-label">비밀번호</label><input className="form-input" type="password" {...sf('password')} /></div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}><label className="form-label">암호화</label>
              <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                {['tls', 'ssl', 'none'].map(v => <label key={v}><input type="radio" name="enc" checked={smtp.encryption === v} onChange={() => setSmtp({ ...smtp, encryption: v })} /> {v.toUpperCase()}</label>)}
              </div>
            </div>
            <div className="form-group" style={{ flex: 1 }}><label className="form-label">발신자명</label><input className="form-input" {...sf('sender_name')} /></div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>알림 항목</div>
          <table style={{ fontSize: 12 }}>
            <thead><tr><th>알림</th><th>수신대상</th><th>활성</th></tr></thead>
            <tbody>
              {notifItems.map(n => (
                <tr key={n.key}>
                  <td>{n.label}</td>
                  <td>
                    {n.key === 'price_set_change' ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <div style={{ fontSize:11, color:'#64748b' }}>지정 그룹의 모든 멤버</div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                          {groups.length === 0 ? <span style={{ fontSize:11, color:'#cbd5e1' }}>그룹 로드 중...</span> : groups.map(g => {
                            const on = (recipients.price_set_change?.group_ids || []).includes(g.id);
                            return (
                              <button key={g.id} type="button" onClick={()=>togglePriceSetGroup(g.id)}
                                style={{ padding:'2px 8px', fontSize:11, borderRadius:10, cursor:'pointer',
                                  background: on ? '#3b82f6' : '#f1f5f9',
                                  color: on ? '#fff' : '#475569',
                                  border: `1px solid ${on ? '#3b82f6' : '#e2e8f0'}` }}>{g.name}</button>
                            );
                          })}
                        </div>
                        {(recipients.price_set_change?.group_ids || []).length === 0 && (
                          <div style={{ fontSize:10, color:'#94a3b8' }}>선택 없음 → 시스템관리자에게 발송</div>
                        )}
                      </div>
                    ) : n.target}
                  </td>
                  <td><input type="checkbox" checked={notifs[n.key] || false} onChange={e => setNotifs({ ...notifs, [n.key]: e.target.checked })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ marginTop:14, padding:12, background:'#f8fafc', borderRadius:8, border:'1px solid #e2e8f0' }}>
        <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>저장 전 SMTP 검증</div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:8 }}>
          <button className="btn btn-outline btn-sm" onClick={()=>testSmtp(false)} disabled={testing || !smtp.host}>
            {testing ? '검사 중...' : '🔌 연결·인증 테스트'}
          </button>
          <span style={{ fontSize:11, color:'#94a3b8' }}>또는 테스트 메일 발송:</span>
          <input className="form-input" style={{ width:240, padding:'5px 8px', fontSize:12 }}
            placeholder="수신 이메일" value={testRecipient} onChange={e=>setTestRecipient(e.target.value)} />
          <button className="btn btn-outline btn-sm" onClick={()=>testSmtp(true)} disabled={testing || !smtp.host || !testRecipient}>
            ✉ 테스트 메일
          </button>
        </div>
        {testResult && (
          <div style={{
            fontSize:12, padding:'8px 10px', borderRadius:6,
            background: testResult.ok ? '#ecfdf5' : '#fef2f2',
            color: testResult.ok ? '#065f46' : '#991b1b',
            border:`1px solid ${testResult.ok ? '#a7f3d0' : '#fecaca'}`,
            whiteSpace:'pre-wrap',
          }}>
            {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
          </div>
        )}

      </div>
      <button className="btn btn-primary" onClick={save} disabled={saving} style={{ marginTop: 12 }}>{saving ? '저장 중...' : '저장'}</button>

      {/* 알림 본문 템플릿 */}
      {tplData && (
        <div style={{marginTop:20,paddingTop:14,borderTop:'1px solid #e2e8f0'}}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:10}}>📝 알림 본문 템플릿</div>
          <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
            <div style={{flex:'1 1 360px',minWidth:340}}>
              <div className="form-group" style={{marginBottom:10}}>
                <label className="form-label">카테고리</label>
                <select className="form-input" value={tplCategory} onChange={e=>setTplCategory(e.target.value)}>
                  {Object.keys(tplEdited).map(cat => (
                    <option key={cat} value={cat}>{tplData.labels[cat] || cat}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{marginBottom:10}}>
                <label className="form-label">제목 (Subject)</label>
                <input className="form-input" value={tplEdited[tplCategory]?.subject || ''}
                  onChange={e=>setTplEdited({...tplEdited, [tplCategory]:{...tplEdited[tplCategory], subject:e.target.value}})} />
              </div>
              <div className="form-group" style={{marginBottom:6}}>
                <label className="form-label">본문 (Body)</label>
                <textarea ref={tplBodyRef} className="form-input" rows={10} style={{fontFamily:'monospace',fontSize:12}}
                  value={tplEdited[tplCategory]?.body || ''}
                  onChange={e=>setTplEdited({...tplEdited, [tplCategory]:{...tplEdited[tplCategory], body:e.target.value}})} />
              </div>
              <div style={{fontSize:11,color:'#64748b',marginBottom:6}}>사용 가능 변수 (클릭하면 본문에 삽입):</div>
              <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:10}}>
                {[...(tplData.vars[tplCategory]||[]), ...(tplData.commonVars||[])].map(v => (
                  <button key={v} type="button" onClick={()=>insertVarAtCursor(v)}
                    style={{padding:'2px 8px',fontSize:11,background:'#eff6ff',color:'#1e40af',border:'1px solid #bfdbfe',borderRadius:10,cursor:'pointer'}}>
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
              <div style={{fontSize:11,color:'#94a3b8',marginBottom:10}}>
                ※ 조건부 블록: <code>{`{{#변수}}내용{{/변수}}`}</code> — 변수에 값이 있을 때만 출력
              </div>
              <div style={{display:'flex',gap:6}}>
                <button className="btn btn-outline btn-sm" onClick={resetCategoryToDefault}>↺ 이 카테고리 기본값 복원</button>
                <div style={{flex:1}} />
                <button className="btn btn-primary btn-sm" onClick={saveTemplates} disabled={tplSaving}>
                  {tplSaving ? '저장 중...' : '💾 템플릿 저장 (전체)'}
                </button>
              </div>
            </div>
            <div style={{flex:'1 1 360px',minWidth:340}}>
              <div style={{fontSize:12,color:'#64748b',marginBottom:6}}>미리보기 (샘플 변수 적용)</div>
              <div style={{border:'1px solid #e2e8f0',borderRadius:8,padding:'10px 12px',background:'#fafbfc'}}>
                <div style={{fontSize:11,color:'#94a3b8',marginBottom:2}}>제목</div>
                <div style={{fontWeight:700,fontSize:13,marginBottom:10,paddingBottom:8,borderBottom:'1px solid #e2e8f0'}}>
                  {renderPreview(tplEdited[tplCategory]?.subject || '') || '(빈 제목)'}
                </div>
                <div style={{fontSize:11,color:'#94a3b8',marginBottom:2}}>본문</div>
                <pre style={{margin:0,fontFamily:'inherit',fontSize:12,whiteSpace:'pre-wrap',color:'#1e293b',lineHeight:1.6}}>{renderPreview(tplEdited[tplCategory]?.body || '') || '(빈 본문)'}</pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 메일 발송 로그 */}
      <div style={{marginTop:20,paddingTop:14,borderTop:'1px solid #e2e8f0'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <div style={{fontSize:14,fontWeight:700}}>📋 메일 발송 로그 <span style={{fontSize:11,color:'#94a3b8'}}>(최근 {logs.length}건 / 전체 {logsTotal}건)</span></div>
          <button className="btn btn-outline btn-sm" onClick={loadLogs} disabled={logsLoading}>{logsLoading ? '로딩...' : '↻ 새로고침'}</button>
        </div>
        <div style={{overflowX:'auto',border:'1px solid #e2e8f0',borderRadius:8,maxHeight:360,overflowY:'auto'}}>
          <table style={{fontSize:12}}>
            <thead style={{position:'sticky',top:0,background:'#f8fafc',zIndex:1}}>
              <tr>
                <th style={{width:155}}>발신시간</th>
                <th style={{width:90}}>사용자명</th>
                <th>수신 이메일</th>
                <th style={{width:120}}>알림 항목</th>
                <th style={{width:80,textAlign:'center'}}>결과</th>
                <th>오류 메시지</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id}>
                  <td style={{whiteSpace:'nowrap'}}>{l.sent_at ? new Date(l.sent_at).toLocaleString('ko-KR', { year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit' }) : '-'}</td>
                  <td>{l.user_name || '-'}</td>
                  <td style={{fontFamily:'monospace',fontSize:11}}>{l.recipient}</td>
                  <td>{l.category_label || l.category || '-'}</td>
                  <td style={{textAlign:'center'}}>
                    <span className={`badge ${l.status === 'success' ? 'badge-green' : 'badge-red'}`}>{l.status === 'success' ? '성공' : '실패'}</span>
                  </td>
                  <td style={{color:'#dc2626',fontSize:11,maxWidth:300,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={l.error_message || ''}>{l.error_message || '-'}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={6} style={{textAlign:'center',padding:24,color:'#94a3b8'}}>발송 로그가 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ===== 9. 사이트 관리 ===== */
function SiteSettingsTab() {
  const [browserTitle, setBrowserTitle] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [homeIconUrl, setHomeIconUrl] = useState('');
  const [loginLogoType, setLoginLogoType] = useState('text');
  const [loginLogoText, setLoginLogoText] = useState('');
  const [loginLogoUrl, setLoginLogoUrl] = useState('');
  const [loginSubtitle, setLoginSubtitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingHome, setUploadingHome] = useState(false);
  const [uploadingLoginLogo, setUploadingLoginLogo] = useState(false);
  const fileRef = useRef(null);
  const homeFileRef = useRef(null);
  const loginLogoFileRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await api.get('/site-info');
        if (!mounted) return;
        setBrowserTitle(data.browser_title || '');
        setFaviconUrl(data.favicon_url || '');
        setHomeIconUrl(data.home_icon_url || '');
        setLoginLogoType(data.login_logo_type || 'text');
        setLoginLogoText(data.login_logo_text || '');
        setLoginLogoUrl(data.login_logo_url || '');
        setLoginSubtitle(data.login_subtitle || '');
      } catch (e) { console.error(e); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const uploadFile = async (file, path) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_ORIGIN}${path}`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || '업로드 실패');
    return data;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const data = await uploadFile(file, '/api/upload/favicon');
      setFaviconUrl(data.url);
    } catch (err) {
      alert(err.message);
    } finally { setUploading(false); }
  };

  const handleHomeIconUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingHome(true);
    try {
      const data = await uploadFile(file, '/api/upload/home-icon');
      setHomeIconUrl(data.url);
    } catch (err) {
      alert(err.message);
    } finally { setUploadingHome(false); }
  };

  const handleLoginLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingLoginLogo(true);
    try {
      const data = await uploadFile(file, '/api/upload/logo');
      setLoginLogoUrl(data.url);
    } catch (err) {
      alert(err.message);
    } finally { setUploadingLoginLogo(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const value = {
        browser_title: browserTitle.trim() || '수산 견적 관리 시스템',
        favicon_url: faviconUrl || null,
        home_icon_url: homeIconUrl || null,
        login_logo_type: loginLogoType === 'image' ? 'image' : 'text',
        login_logo_text: loginLogoText.trim() || 'SOOSAN ERP',
        login_logo_url: loginLogoUrl || null,
        login_subtitle: loginSubtitle.trim() || '수산 견적 관리 시스템',
      };
      const saved = await api.put('/site-info', value);
      applySiteInfo(saved);
      alert('사이트 설정이 저장되었습니다.');
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="card"><div className="text-muted" style={{padding:24,textAlign:'center'}}>불러오는 중...</div></div>;

  return (
    <div>
      <div className="card">
        <div className="card-title mb-8">사이트 관리</div>
        <div className="text-muted" style={{marginBottom:16,fontSize:12}}>
          브라우저 탭에 표시되는 제목과 파비콘을 설정합니다. 저장 시 즉시 모든 페이지(로그인 화면 포함)에 적용됩니다.
        </div>

        <div className="form-group">
          <label className="form-label">브라우저 탭 제목</label>
          <input className="form-input" value={browserTitle} onChange={e => setBrowserTitle(e.target.value)} placeholder="수산 견적 관리 시스템" maxLength={100} />
          <div className="text-muted" style={{fontSize:11,marginTop:4}}>비워두면 기본값 "수산 견적 관리 시스템" 이 적용됩니다.</div>
        </div>

        <div className="form-group">
          <label className="form-label">파비콘</label>
          <div style={{display:'flex',alignItems:'center',gap:16}}>
            <div style={{width:48,height:48,border:'1px solid #e2e8f0',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',background:'#f8fafc'}}>
              {faviconUrl
                ? <img src={faviconUrl} alt="favicon" style={{maxWidth:32,maxHeight:32}} />
                : <span style={{fontSize:11,color:'#94a3b8'}}>없음</span>}
            </div>
            <input ref={fileRef} type="file" accept=".png,.ico,.svg,image/png,image/x-icon,image/svg+xml" style={{display:'none'}} onChange={handleUpload} />
            <button className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? '업로드 중...' : '파일 선택'}
            </button>
            {faviconUrl && (
              <button className="btn btn-outline btn-sm" onClick={() => setFaviconUrl('')}>제거</button>
            )}
          </div>
          <div className="text-muted" style={{fontSize:11,marginTop:4}}>PNG / ICO / SVG, 최대 1MB. 브라우저 캐시로 인해 변경 후 강제 새로고침이 필요할 수 있습니다.</div>
        </div>

        <div className="form-group">
          <label className="form-label">홈화면 추가 아이콘</label>
          <div style={{display:'flex',alignItems:'center',gap:16}}>
            <div style={{width:48,height:48,border:'1px solid #e2e8f0',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',background:'#f8fafc'}}>
              {homeIconUrl
                ? <img src={homeIconUrl} alt="home icon" style={{maxWidth:40,maxHeight:40,borderRadius:6}} />
                : <span style={{fontSize:11,color:'#94a3b8'}}>없음</span>}
            </div>
            <input ref={homeFileRef} type="file" accept=".png,image/png" style={{display:'none'}} onChange={handleHomeIconUpload} />
            <button className="btn btn-outline btn-sm" onClick={() => homeFileRef.current?.click()} disabled={uploadingHome}>
              {uploadingHome ? '업로드 중...' : '파일 선택'}
            </button>
            {homeIconUrl && (
              <button className="btn btn-outline btn-sm" onClick={() => setHomeIconUrl('')}>제거</button>
            )}
          </div>
          <div className="text-muted" style={{fontSize:11,marginTop:4}}>
            PNG, 최대 2MB. 512x512 권장. iOS는 다음 "홈 화면에 추가" 시 적용, Android 는 이미 추가된 앱을 다시 추가해야 새 아이콘이 반영됩니다.
          </div>
        </div>

        <div style={{borderTop:'1px solid #e2e8f0',margin:'16px 0',paddingTop:16}}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:10}}>로그인 화면</div>

          <div className="form-group">
            <label className="form-label">로고 타입</label>
            <div style={{display:'flex',gap:16}}>
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13}}>
                <input type="radio" checked={loginLogoType === 'text'} onChange={() => setLoginLogoType('text')} /> 텍스트
              </label>
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13}}>
                <input type="radio" checked={loginLogoType === 'image'} onChange={() => setLoginLogoType('image')} /> 이미지
              </label>
            </div>
          </div>

          {loginLogoType === 'text' ? (
            <div className="form-group">
              <label className="form-label">로고 텍스트</label>
              <input className="form-input" value={loginLogoText} onChange={e => setLoginLogoText(e.target.value)} placeholder="SOOSAN ERP" maxLength={50} />
              <div className="text-muted" style={{fontSize:11,marginTop:4}}>첫 단어는 파란색으로 강조됩니다. 예: "SOOSAN ERP" → <span style={{color:'#3b82f6',fontWeight:700}}>SOOSAN</span> <span style={{fontWeight:700}}>ERP</span></div>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">로고 이미지</label>
              <div style={{display:'flex',alignItems:'center',gap:16}}>
                <div style={{minWidth:120,minHeight:48,border:'1px solid #e2e8f0',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',background:'#f8fafc',padding:6}}>
                  {loginLogoUrl
                    ? <img src={loginLogoUrl} alt="login logo" style={{maxHeight:40,maxWidth:200}} />
                    : <span style={{fontSize:11,color:'#94a3b8'}}>없음</span>}
                </div>
                <input ref={loginLogoFileRef} type="file" accept=".png,.svg,image/png,image/svg+xml" style={{display:'none'}} onChange={handleLoginLogoUpload} />
                <button className="btn btn-outline btn-sm" onClick={() => loginLogoFileRef.current?.click()} disabled={uploadingLoginLogo}>
                  {uploadingLoginLogo ? '업로드 중...' : '파일 선택'}
                </button>
                {loginLogoUrl && (
                  <button className="btn btn-outline btn-sm" onClick={() => setLoginLogoUrl('')}>제거</button>
                )}
              </div>
              <div className="text-muted" style={{fontSize:11,marginTop:4}}>PNG / SVG, 최대 2MB.</div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">부제목</label>
            <input className="form-input" value={loginSubtitle} onChange={e => setLoginSubtitle(e.target.value)} placeholder="수산 견적 관리 시스템" maxLength={100} />
          </div>
        </div>

        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '설정 저장'}</button>
      </div>
    </div>
  );
}

/* ===== Main Settings Page ===== */
/* ===== 패치 관리 탭 ===== */
function PatchTab() {
  const [version, setVersion] = useState('');
  const [history, setHistory] = useState([]);
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState('idle'); // idle | validating | validated | applying | done
  const [validation, setValidation] = useState(null); // { ok, results, manifest, tmpPath, extractDir, hasFrontend, hasBackend, hasSql }
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => { loadVersion(); loadHistory(); }, []);

  const loadVersion = () => api.get('/patches/version').then(d => setVersion(d.version)).catch(() => {});
  const loadHistory = () => api.get('/patches/history').then(setHistory).catch(() => {});

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setValidation(null);
    setStage('idle');
    setError('');
    setApplyResult(null);
  };

  const handleValidate = async () => {
    if (!file) return;
    setStage('validating');
    setError('');
    setValidation(null);
    try {
      const fd = new FormData();
      fd.append('patch', file);
      const token = localStorage.getItem('token');
      const res = await fetch('/api/patches/validate', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '검사 실패');
      setValidation(data);
      setStage('validated');
    } catch (e) {
      setError(e.message);
      setStage('idle');
    }
  };

  const handleApply = async () => {
    if (!validation) return;
    if (!window.confirm(`v${validation.manifest?.version} 패치를 적용하시겠습니까?\n롤백 스냅샷이 자동 생성됩니다.`)) return;
    setStage('applying');
    setError('');
    try {
      const data = await api.post('/patches/apply', { tmpPath: validation.tmpPath, extractDir: validation.extractDir });
      setApplyResult(data);
      setStage('done');
      setValidation(null);
      setFile(null);
      loadVersion();
      loadHistory();
      if (data.needsRestart) {
        alert(`패치 적용 완료 (v${data.version})\n\n백엔드 파일이 변경되어 서버가 재시작됩니다.\n약 5초 후 페이지를 새로고침하세요.`);
      }
    } catch (e) {
      setError(e.message);
      setStage('validated');
    }
  };

  const handleRollback = async (patch) => {
    if (!window.confirm(`v${patch.version} 패치를 롤백하시겠습니까?\n이 패치 적용 이전 상태로 되돌아갑니다.`)) return;
    try {
      const data = await api.post(`/patches/${patch.id}/rollback`, {});
      loadVersion();
      loadHistory();
      if (data.needsRestart) {
        alert(`롤백 완료 (→ v${data.version})\n서버가 재시작됩니다. 약 5초 후 새로고침하세요.`);
      } else {
        alert(`롤백 완료 (→ v${data.version})`);
      }
    } catch (e) { alert(e.message); }
  };

  const statusBadge = (s) => {
    if (s === 'success') return <span className="badge badge-blue">적용됨</span>;
    if (s === 'rolled_back') return <span className="badge" style={{ background: '#f1f5f9', color: '#64748b' }}>롤백됨</span>;
    return <span className="badge badge-red">{s}</span>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* 현재 버전 */}
      <div className="card">
        <div className="card-header"><div className="card-title">현재 버전</div></div>
        <div style={{ padding: '8px 0', fontSize: 28, fontWeight: 700, color: '#3b82f6' }}>
          v{version || '—'}
        </div>
      </div>

      {/* 패치 업로드 & 적용 */}
      <div className="card">
        <div className="card-header"><div className="card-title">패치 업로드</div></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input ref={fileRef} type="file" accept=".zip,.tar.gz,.tgz,.gz" onChange={handleFileChange} style={{ display: 'none' }} />
            <button className="btn btn-outline" onClick={() => fileRef.current?.click()}>파일 선택</button>
            <span style={{ fontSize: 13, color: '#64748b' }}>{file ? file.name : 'zip 또는 tar.gz 파일'}</span>
            {file && stage === 'idle' && (
              <button className="btn btn-primary btn-sm" onClick={handleValidate}>검사 실행</button>
            )}
            {stage === 'validating' && <span style={{ fontSize: 13, color: '#64748b' }}>검사 중...</span>}
          </div>

          {/* 검사 결과 */}
          {validation && (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', background: validation.ok ? '#f0fdf4' : '#fef2f2', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: validation.ok ? '#16a34a' : '#dc2626' }}>
                {validation.ok ? '✓ 검사 통과 — 패치 적용 가능' : '✗ 검사 실패 — 아래 항목 확인'}
              </div>
              <table style={{ width: '100%', fontSize: 13 }}>
                <tbody>
                  {validation.results.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 16px', width: 24 }}>{r.ok ? '✓' : '✗'}</td>
                      <td style={{ padding: '8px 0', fontWeight: 500, width: 140 }}>{r.label}</td>
                      <td style={{ padding: '8px 16px', color: r.ok ? '#374151' : '#dc2626' }}>{r.msg}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {validation.ok && (
                <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" onClick={handleApply} disabled={stage === 'applying'}>
                    {stage === 'applying' ? '적용 중...' : '패치 적용'}
                  </button>
                  <button className="btn btn-outline" onClick={() => { setStage('idle'); setValidation(null); setFile(null); }}>취소</button>
                </div>
              )}
            </div>
          )}

          {stage === 'done' && applyResult && (
            <div style={{ padding: 12, background: '#f0fdf4', borderRadius: 8, color: '#16a34a', fontWeight: 500 }}>
              ✓ v{applyResult.version} 패치 적용 완료
              {applyResult.needsRestart && ' — 서버 재시작 중'}
            </div>
          )}

          {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}
        </div>
      </div>

      {/* 패치 이력 */}
      <div className="card">
        <div className="card-header"><div className="card-title">패치 이력</div></div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>버전</th><th>패치명</th><th>적용일시</th><th>적용자</th><th>포함</th><th>상태</th><th>작업</th>
              </tr>
            </thead>
            <tbody>
              {history.map(p => (
                <tr key={p.id}>
                  <td className="fw-bold">v{p.version}</td>
                  <td>{p.patch_name || '—'}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{p.applied_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td>{p.applied_by_name || '—'}</td>
                  <td style={{ fontSize: 11 }}>
                    {[p.includes_frontend && 'FE', p.includes_backend && 'BE', p.includes_sql && 'SQL'].filter(Boolean).join(' ')}
                  </td>
                  <td>{statusBadge(p.status)}</td>
                  <td>
                    {p.status === 'success' && p.snapshot_path && (
                      <button className="btn btn-outline btn-sm" onClick={() => handleRollback(p)}>롤백</button>
                    )}
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>적용된 패치가 없습니다.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('template');

  const renderTab = () => {
    switch (activeTab) {
      case 'template': return <TemplateTab />;
      case 'estimate': return <EstimateSettingTab />;
      case 'groups': return <GroupsTab />;
      case 'partproduct': return <PartProductTab />;
      case 'backup': return <BackupTab />;
      case 'email': return <EmailTab />;
      case 'site': return <SiteSettingsTab />;
      case 'patch': return <PatchTab />;
      default: return null;
    }
  };

  return (
    <div>
      <div className="tabs">
        {tabList.map(t => (
          <div key={t.key} className={`tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>{t.label}</div>
        ))}
      </div>
      {renderTab()}
    </div>
  );
}
